import {Action, di} from "@dp-instant/core";
import {TypesFlow} from "@dp-instant/interfaces";
import {LevelEndAction} from "@dp-instant/gameflow";
import {UIEvents} from "@dp-instant/ui";
import {GameModel} from "@game/coregame/model/GameModel";
import {TypesGame} from "@game/TypesGame";
import {PopupType} from "@game/ui/PopupType";
import {inject, injectable} from "inversify";
import {SpritesPool} from "@game/coregame/utils/SpritesPool";
import {GameState} from "@game/coregame/model/GameState";
import {ShowWinPopupAction} from "./ShowWinPopupAction";
import {SIOConstants} from "@game/constants";
import {Playdeck} from "@game/coregame/utils/Playdeck";

// Only bundled when SOCIAL=tg (dead code elimination for other platforms)
const _submitMatchToServer = __SOCIAL__ === "tg"
    ? async (model: GameModel, win: boolean, startTime: number) => {
        try {
            const { apiClient } = await import("@game/api/ApiClient");
            const continent = model.currentContinent;
            const mapId = continent?.data?.id ?? "unknown";
            const stage = model.cookie.currentStage;
            const botPreset = String(model.cookie.botLevel ?? 0);
            const seed = Math.floor(Math.random() * 2147483647);
            const result = win ? "win" : "lose";
            // Deterministic hash for anti-cheat (matches server validation logic)
            const hashData = `${seed}:${mapId}:${stage}:${result}`;
            const msgBuffer = new TextEncoder().encode(hashData);
            const hashBuffer = await crypto.subtle.digest("SHA-256", msgBuffer);
            const replayHash = Array.from(new Uint8Array(hashBuffer))
                .map(b => b.toString(16).padStart(2, "0"))
                .join("");
            await apiClient.submitMatch({
                mapId,
                stage,
                botPreset,
                result,
                durationMs: Date.now() - startTime,
                replaySeed: seed,
                replayHash
            });
        } catch (err) {
            console.warn("[TG] Match submit failed:", err);
        }
    }
    : async () => {};

@injectable()
export class LevelEndActionSIO extends LevelEndAction {
    @inject(TypesGame.model)
    protected model: GameModel;

    @inject(TypesGame.spritesPool)
    private assets: SpritesPool;

    @inject(TypesFlow.LevelNext)
    private levelNext: Action;

    async launch(win = false) {
        const _matchStartTime = Date.now();
        const {meta, cookie} = this.model;
        cookie.absoluteTryNum++;
        SIOConstants.REWARD_AD_PLAYED.delete("boosters");
        await di.get<Action>(TypesGame.actions.tournamentPostScore)?.run();
        di.get<Action<boolean>>(TypesGame.actions.updateBotLevelAction)?.run(win);
        _submitMatchToServer(this.model, win, _matchStartTime);

        // Trigger immediate sync to server on level end (TG only)
        if (__SOCIAL__ === "tg" && (window as any).__tgImmediateSync) {
            (window as any).__tgImmediateSync();
        }

        // Show interstitial ad after battle (TG only, non-blocking)
        if (__SOCIAL__ === "tg") {
            import("@game/ads/AdManager").then(({adManager}) => {
                adManager.showInterstitial();
            }).catch(() => {});
        }

        if (win && cookie.absoluteLevelNum === 1) {
            Playdeck.sendProgress(4);
        }

        if (win) {
            Playdeck.sendProgress(cookie.absoluteTryNum);
        }

        if (win) {
            this.model.cookie.absoluteLevelNum++;
            if (this.social.inSolo) {
                await this.popupWin();
                await this.popupLevelComplete();
                await this.popupGift();
                await this.levelNext.run();
                this.model.state = GameState.LOBBY;
            } else {
                await this.popupBattleResults();
                await this.popupLevelComplete();
                await this.updateTournament();
                await this.social.playSolo();
                await this.levelNext.run();
                this.model.state = GameState.LOBBY;
            }
        } else {
            this.dispatch(UIEvents.POPUP, {
                id: PopupType.LOSE,
                props: {coins: meta.getReward() * meta.loseMultiplier}
            });
        }
        this.assets.purge();
    }
    private async popupGift() {
        await di.get<Action>(TypesGame.actions.giftPopup)?.run();
    }
    private async updateTournament(): Promise<boolean> {
        return await di.get<Action>(TypesGame.actions.tournamentReShare).run();
    }
    private async createTournament(): Promise<boolean> {
        return await di.get<Action>(TypesGame.actions.tournamentCreate).run();
    }
    private async popupWin() {
        await di
            .get<ShowWinPopupAction>(TypesGame.actions.winPopup)
            ?.run({id: PopupType.WIN_LEVEL});
    }
    private async popupLevelComplete() {
        await di.get<Action>(TypesGame.actions.levelCompletePopup)?.run();
    }
    private async popupBattleResults() {
        await di.get<Action<boolean>>(TypesGame.actions.battleResultsPopup)?.run(this.data);
    }
    protected async submitScore(): Promise<void> {
        // TG uses server-side leaderboards via match submit API,
        // skip framework's AppModel.post() which has no configured host for TG
        if (__SOCIAL__ === "tg") return;

        const {currentContinent} = this.model;
        /**
         * !!! IMPORTANT !!!
         * scoreSession - is used to compare users between each other in the VIRAL LEADERBOARD – must be integer
         * `postSessionScore` method actually triggers the VIRAL LEADERBOARD window to popup (create/update)
         */
        this.social.me.scoreSession = currentContinent.getTotalScore();
        if (this.social.inSolo) {
            this.leaderboardGlobal?.submit(this.model.absoluteLevelNum, {
                continent: currentContinent.data.id
            });
        }
    }

    protected needToShowAD(): boolean {
        return false;
    }
}
